// src/utils/ws_peticiones.js
// Cliente WebSocket para el Production Display System (PDS) de Divisan.
// Sin autenticación — la pantalla de producción es acceso libre.
//
// Es una pantalla desatendida (TV en planta), así que la reconexión NO debe
// rendirse nunca: antes había un tope de 5 intentos que, al agotarse, dejaba
// el tablero muerto hasta que alguien recargara la página a mano. Ahora
// reintenta indefinidamente con backoff exponencial real (tope 30s).

let socket = null
let reconnectTimer = null
let _config = null

let retries = 0
const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30000

const WS_BASE = process.env.VUE_APP_WS ||
  `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`

// ── Conexión PDS ─────────────────────────────────────────────────────────────
/**
 * Conecta al WebSocket del Production Display System.
 * Reconexión automática indefinida con backoff exponencial (máx 30s).
 *
 * @param {Object}   config
 * @param {Function} config.onMessage  - recibe el objeto JSON parseado
 * @param {Function} [config.onOpen]
 * @param {Function} [config.onClose]
 * @param {Function} [config.onError]  - recibe (event, retries)
 */
export function connectPDS({ onMessage, onOpen, onClose, onError }) {
  _config = { onMessage, onOpen, onClose, onError }
  _connect()
}

function _connect() {
  socket = new WebSocket(`${WS_BASE}/ws/pedidos/`)
  const cfg = _config  // captura local para evitar race conditions

  socket.onopen = () => {
    retries = 0
    _clearReconnectTimer()
    console.info("[WS Divisan] Conectado al PDS")
    if (cfg?.onOpen) cfg.onOpen()
  }

  socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      if (cfg?.onMessage) cfg.onMessage(data)
    } catch (e) {
      console.error("[WS Divisan] Error parseando mensaje:", e)
    }
  }

  socket.onclose = (event) => {
    if (cfg?.onClose) cfg.onClose(event)

    // Reconexión automática solo si no fue un cierre limpio (closePDS()
    // cierra con código 1000 explícitamente para evitar esto al desmontar).
    // Sin tope de intentos: es una pantalla desatendida, debe seguir
    // intentando para siempre en vez de quedarse muerta hasta un refresh manual.
    if (event.code !== 1000) {
      retries++
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** (retries - 1), RECONNECT_MAX_MS)
      console.warn(`[WS Divisan] Reconectando en ${delay}ms (intento ${retries})`)
      reconnectTimer = setTimeout(_connect, delay)
    }
  }

  socket.onerror = (error) => {
    console.error("[WS Divisan] Error:", error)
    if (cfg?.onError) cfg.onError(error, retries)
  }
}

// ── Cerrar conexión ──────────────────────────────────────────────────────────
/**
 * Cierra la conexión limpiamente sin reconexión automática.
 */
export function closePDS() {
  _clearReconnectTimer()
  retries = 0
  if (socket) {
    socket.close(1000, "Cierre manual")
    socket = null
  }
  _config = null
}

// ── Estado ───────────────────────────────────────────────────────────────────
/**
 * Retorna el estado actual del socket.
 * @returns {'CONNECTING'|'OPEN'|'CLOSING'|'CLOSED'|'NO_SOCKET'}
 */
export function getPDSState() {
  if (!socket) return "NO_SOCKET"
  return ["CONNECTING", "OPEN", "CLOSING", "CLOSED"][socket.readyState] ?? "UNKNOWN"
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function _clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}